import React from 'react';
import { Box, Typography, Button, Breadcrumbs, Link } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';

const PageHeader = ({
  title,
  subtitle,
  icon: Icon,
  iconColor = '#5C6BC0',
  breadcrumbs = [],
  action,
  actionLabel,
  actionIcon: ActionIcon,
  actionVariant = 'contained',
  actionColor = 'primary',
  extraActions,
  children,
}) => {
  const navigate = useNavigate();

  return (
    <Box sx={{ mb: 4 }}>
      {/* Breadcrumbs */}
      {breadcrumbs.length > 0 && (
        <Breadcrumbs
          separator={<NavigateNextIcon fontSize="small" />}
          sx={{ mb: 2 }}
        >
          {breadcrumbs.map((crumb, index) => (
            crumb.path ? (
              <Link
                key={index}
                color="inherit"
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  navigate(crumb.path);
                }}
                sx={{
                  textDecoration: 'none',
                  '&:hover': { textDecoration: 'underline' },
                }}
              >
                {crumb.label}
              </Link>
            ) : (
              <Typography key={index} color="text.primary">
                {crumb.label}
              </Typography>
            )
          ))}
        </Breadcrumbs>
      )}

      {/* Header Row */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 2,
        }}
      >
        {/* Title Section */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {Icon && (
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: 3,
                background: `linear-gradient(135deg, ${iconColor}20 0%, ${iconColor}10 100%)`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: `2px solid ${iconColor}30`,
              }}
            >
              <Icon sx={{ fontSize: 28, color: iconColor }} />
            </Box>
          )}
          <Box>
            <Typography
              variant="h4"
              fontWeight="700"
              sx={{
                background: `linear-gradient(90deg, ${iconColor}, ${iconColor}CC)`,
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              {title}
            </Typography>
            {subtitle && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {subtitle}
              </Typography>
            )}
          </Box>
        </Box>

        {/* Actions */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {extraActions}
          {action && (
            <Button
              variant={actionVariant}
              color={actionColor}
              onClick={action}
              startIcon={ActionIcon && <ActionIcon />}
              sx={{
                px: 3,
                py: 1.2,
                boxShadow: actionVariant === 'contained' ? '0 4px 14px rgba(0,0,0,0.15)' : 'none',
              }}
            >
              {actionLabel}
            </Button>
          )}
        </Box>

        {/* Custom Children */}
        {children}
      </Box>
    </Box>
  );
};

export default PageHeader;
